/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { Box, Flex, IconButton, IconEdit, IconLoading, IconTrash, SquareBlock } from "@powerledger/ui-component-lib";
import { map } from "lodash";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { convertToCompanyContactInput, personalSchema } from "@/app/lib/validation";
import { ReactComponent as User } from "@/assets/images/user.svg";
import { ReactComponent as UserAdd } from "@/assets/images/userAdd.svg";

import { ContactType } from "../../types/generated/graphql";
import { BeneficialOwnerProps } from "./beneficial-owners.types";
import { RemoveBeneficialOwnerModal } from "./remove-beneficial-owners-modal";

export const BeneficialOwners: FC<BeneficialOwnerProps> = ({
  company,
  loading,
  noEdit,
  handleDeleteBo = () => {},
  handleOpenForm = () => {},
  boIdToRemove = "",
  setBoIdToRemove = () => {},
  deleteBoLoading = false,
  disabledBos,
}) => {
  const { t } = useTranslation();

  const beneficialOwners = useMemo(
    () => company?.contacts?.filter((contact) => contact.type === ContactType.BeneficialOwner) || [],
    [company?.contacts],
  );

  return (
    <Box sx={{ position: "relative" }}>
      <Flex sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ color: "textDarker", fontSize: 1, flex: 2 }}>{t("Applicant Beneficial Owners")}</Box>
      </Flex>
      <Box sx={{ mt: 3 }}>
        {loading && <IconLoading />}
        {(noEdit || !!disabledBos?.length) && beneficialOwners.length < 1 && (
          <Flex sx={{ justifyContent: "center", color: "textDark", fontSize: 0, fontStyle: "italic" }}>
            {t("-- No beneficial owner has been added --")}
          </Flex>
        )}
        <Flex sx={{ flexWrap: "wrap", mx: -2 }}>
          {map(beneficialOwners, (bo) => (
            <SquareBlock
              key={bo.id}
              error={
                personalSchema.isValidSync(convertToCompanyContactInput(ContactType.BeneficialOwner, bo))
                  ? ""
                  : "Error - Incomplete details"
              }
              sx={{
                width: ["100%", null, "50%"],
                px: 2,
                maxWidth: 300,
                mb: 3,
                div: {
                  background: "transparent",
                  boxShadow: "none",
                },
              }}
            >
              <>
                <Box
                  sx={{
                    maxWidth: "33.33%",
                    svg: {
                      path: {
                        stroke: "primary.500",
                      },
                    },
                  }}
                >
                  <User />
                </Box>
                <Box sx={{ fontSize: 1, mt: 4 }}>
                  {t("{{beneficialOwnerName}}", {
                    beneficialOwnerName: `${bo?.firstName ? bo?.firstName : ""} ${bo?.lastName ? bo?.lastName : ""}`,
                  })}
                </Box>
                <Flex sx={{ alignItems: "center", mt: 3 }}>
                  {!noEdit && !disabledBos?.includes(bo.id) && (
                    <>
                      <IconButton
                        disabled={noEdit}
                        onClick={() => {
                          handleOpenForm(bo);
                        }}
                        data-testid={"beneficial-owners--edit"}
                      >
                        <IconEdit stroke="primary.500" size={7} />
                      </IconButton>

                      <IconButton
                        disabled={noEdit}
                        onClick={() => {
                          setBoIdToRemove(bo.id);
                        }}
                        data-testid={"beneficial-owners--remove"}
                      >
                        <IconTrash stroke="error.500" size={7} />
                      </IconButton>
                    </>
                  )}
                </Flex>
              </>
            </SquareBlock>
          ))}
          {company?.id && !noEdit && beneficialOwners?.length < 4 && !disabledBos?.length && (
            <SquareBlock
              key="addNew"
              isEmpty
              onClick={() => handleOpenForm()}
              sx={{
                width: ["100%", null, "50%"],
                px: 2,
                maxWidth: 300,
                mb: 3,
                div: {
                  background: "transparent",
                  boxShadow: "none",
                },
              }}
            >
              <>
                <Box
                  sx={{
                    svg: {
                      path: {
                        stroke: "primary.500",
                      },
                    },
                  }}
                >
                  <UserAdd />
                </Box>
                <Box
                  sx={{
                    fontSize: 1,
                    pt: 4,
                  }}
                  data-testid={"beneficial-owners--add-new"}
                >
                  {t("Add New")}
                </Box>
              </>
            </SquareBlock>
          )}
        </Flex>
      </Box>
      <RemoveBeneficialOwnerModal
        visible={!!boIdToRemove}
        handleDeleteBo={handleDeleteBo}
        loading={deleteBoLoading}
        closeModal={() => {
          setBoIdToRemove("");
        }}
      />
    </Box>
  );
};
